import React, { useEffect, useState } from 'react';
import {
  RefreshCw,
  Search,
  Plus,
  UserPlus,
  Users as UsersIcon,
  Mail,
  Lock,
  Filter,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Power,
  KeyRound,
} from 'lucide-react';

const API_URL = '/api';

function PerfilChip({ perfil }) {
  const map = {
    admin: { className: 'chip-brand', label: 'Administrador' },
    supervisor: { className: 'chip-info', label: 'Supervisor' },
    atendente: { className: 'chip-neutral', label: 'Atendente' },
  };
  const m = map[perfil] || { className: 'chip-neutral', label: perfil };
  return <span className={m.className}>{m.label}</span>;
}

export default function Usuarios({ apiFetch, usuarioLogado }) {
  const [usuarios, setUsuarios] = useState([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(true);
  const [erroUsuarios, setErroUsuarios] = useState(null);
  const [mensagemUsuarios, setMensagemUsuarios] = useState(null);

  const [nomeNovoUsuario, setNomeNovoUsuario] = useState('');
  const [emailNovoUsuario, setEmailNovoUsuario] = useState('');
  const [senhaNovoUsuario, setSenhaNovoUsuario] = useState('');
  const [perfilNovoUsuario, setPerfilNovoUsuario] = useState('atendente');
  const [criandoUsuario, setCriandoUsuario] = useState(false);

  const [perfisEdicao, setPerfisEdicao] = useState({});
  const [senhasEdicao, setSenhasEdicao] = useState({});
  const [acaoUsuarioId, setAcaoUsuarioId] = useState(null);

  const [buscaUsuarios, setBuscaUsuarios] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');

  const [paginaUsuarios, setPaginaUsuarios] = useState(1);
  const usuariosPorPagina = 5;

  const carregarUsuarios = async () => {
    try {
      setCarregandoUsuarios(true);
      setErroUsuarios(null);

      const response = await apiFetch(`${API_URL}/usuarios`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao carregar usuários');

      setUsuarios(data);
      const mapaPerfis = {};
      data.forEach((u) => { mapaPerfis[u.id] = u.perfil; });
      setPerfisEdicao(mapaPerfis);
    } catch (err) {
      setErroUsuarios(err.message || 'Erro ao carregar usuários');
    } finally {
      setCarregandoUsuarios(false);
    }
  };

  const criarUsuario = async (e) => {
    e.preventDefault();
    try {
      setCriandoUsuario(true);
      setErroUsuarios(null);
      setMensagemUsuarios(null);

      const response = await apiFetch(`${API_URL}/usuarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nomeNovoUsuario,
          email: emailNovoUsuario,
          senha: senhaNovoUsuario,
          perfil: perfilNovoUsuario,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao criar usuário');

      setMensagemUsuarios('Usuário criado com sucesso.');
      setNomeNovoUsuario('');
      setEmailNovoUsuario('');
      setSenhaNovoUsuario('');
      setPerfilNovoUsuario('atendente');
      await carregarUsuarios();
    } catch (err) {
      setErroUsuarios(err.message || 'Erro ao criar usuário');
    } finally {
      setCriandoUsuario(false);
    }
  };

  const salvarPerfilUsuario = async (usuarioId) => {
    try {
      setAcaoUsuarioId(usuarioId);
      setErroUsuarios(null);
      setMensagemUsuarios(null);
      const perfil = perfisEdicao[usuarioId];
      const response = await apiFetch(`${API_URL}/usuarios/${usuarioId}/perfil`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ perfil }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao alterar perfil');
      setMensagemUsuarios('Perfil atualizado com sucesso.');
      await carregarUsuarios();
    } catch (err) {
      setErroUsuarios(err.message || 'Erro ao alterar perfil');
    } finally {
      setAcaoUsuarioId(null);
    }
  };

  const alternarStatusUsuario = async (usuario) => {
    try {
      setAcaoUsuarioId(usuario.id);
      setErroUsuarios(null);
      setMensagemUsuarios(null);
      const response = await apiFetch(`${API_URL}/usuarios/${usuario.id}/ativo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativo: !usuario.ativo }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao atualizar status');
      setMensagemUsuarios(usuario.ativo ? 'Usuário desativado.' : 'Usuário ativado.');
      await carregarUsuarios();
    } catch (err) {
      setErroUsuarios(err.message || 'Erro ao atualizar status');
    } finally {
      setAcaoUsuarioId(null);
    }
  };

  const alterarSenhaUsuario = async (usuarioId) => {
    try {
      setAcaoUsuarioId(usuarioId);
      setErroUsuarios(null);
      setMensagemUsuarios(null);
      const senha = senhasEdicao[usuarioId] || '';
      if (senha.length < 6) throw new Error('A senha deve ter pelo menos 6 caracteres');
      const response = await apiFetch(`${API_URL}/usuarios/${usuarioId}/senha`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senha }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao alterar senha');
      setMensagemUsuarios('Senha atualizada com sucesso.');
      setSenhasEdicao((prev) => ({ ...prev, [usuarioId]: '' }));
    } catch (err) {
      setErroUsuarios(err.message || 'Erro ao alterar senha');
    } finally {
      setAcaoUsuarioId(null);
    }
  };

  useEffect(() => { carregarUsuarios(); }, []);

  const usuariosFiltrados = usuarios.filter((u) => {
    const termo = buscaUsuarios.trim().toLowerCase();
    const bateBusca = !termo ||
      u.nome.toLowerCase().includes(termo) ||
      u.email.toLowerCase().includes(termo);
    const batePerfil = filtroPerfil === 'todos' || u.perfil === filtroPerfil;
    const bateStatus =
      filtroStatus === 'todos' ||
      (filtroStatus === 'ativos' && u.ativo) ||
      (filtroStatus === 'inativos' && !u.ativo);
    return bateBusca && batePerfil && bateStatus;
  });

  const totalPaginas = Math.max(1, Math.ceil(usuariosFiltrados.length / usuariosPorPagina));
  const paginaSegura = Math.min(paginaUsuarios, totalPaginas);
  const iIni = (paginaSegura - 1) * usuariosPorPagina;
  const usuariosPaginados = usuariosFiltrados.slice(iIni, iIni + usuariosPorPagina);

  useEffect(() => { setPaginaUsuarios(1); }, [buscaUsuarios, filtroPerfil, filtroStatus]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Usuários</h1>
          <p className="text-sm text-ink-subtle mt-1">
            Gerencie acessos, perfis e senhas da equipe.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={carregarUsuarios}
          disabled={carregandoUsuarios}
        >
          {carregandoUsuarios ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {carregandoUsuarios ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {erroUsuarios && (
        <div className="flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erroUsuarios}</span>
        </div>
      )}
      {mensagemUsuarios && (
        <div className="flex items-start gap-2 rounded-xl bg-success-50 text-success-700 px-3.5 py-2.5 text-sm">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{mensagemUsuarios}</span>
        </div>
      )}

      <section className="card p-5">
        <header className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Filtros</h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
            <input
              type="text"
              placeholder="Nome ou e-mail"
              value={buscaUsuarios}
              onChange={(e) => setBuscaUsuarios(e.target.value)}
              className="input pl-9"
            />
          </div>
          <select
            value={filtroPerfil}
            onChange={(e) => setFiltroPerfil(e.target.value)}
            className="input"
          >
            <option value="todos">Todos os perfis</option>
            <option value="admin">Administrador</option>
            <option value="supervisor">Supervisor</option>
            <option value="atendente">Atendente</option>
          </select>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="input"
          >
            <option value="todos">Todos os status</option>
            <option value="ativos">Somente ativos</option>
            <option value="inativos">Somente inativos</option>
          </select>
        </div>

        <p className="text-xs text-ink-subtle mt-3">
          {usuariosFiltrados.length} usuário(s) encontrado(s)
        </p>
      </section>

      <section className="card p-5">
        <header className="flex items-center gap-2 mb-1">
          <UserPlus size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Novo usuário</h2>
        </header>
        <p className="text-xs text-ink-subtle mb-4">Crie um acesso para a equipe.</p>

        <form onSubmit={criarUsuario} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="label">Nome *</label>
              <input
                type="text"
                value={nomeNovoUsuario}
                onChange={(e) => setNomeNovoUsuario(e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="label">E-mail *</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                <input
                  type="email"
                  value={emailNovoUsuario}
                  onChange={(e) => setEmailNovoUsuario(e.target.value)}
                  className="input pl-9"
                  required
                />
              </div>
            </div>
            <div>
              <label className="label">Senha *</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                <input
                  type="password"
                  value={senhaNovoUsuario}
                  onChange={(e) => setSenhaNovoUsuario(e.target.value)}
                  className="input pl-9"
                  required
                />
              </div>
            </div>
            <div>
              <label className="label">Perfil *</label>
              <select
                value={perfilNovoUsuario}
                onChange={(e) => setPerfilNovoUsuario(e.target.value)}
                className="input"
              >
                <option value="atendente">Atendente</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
          <div>
            <button type="submit" className="btn-primary" disabled={criandoUsuario}>
              {criandoUsuario ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {criandoUsuario ? 'Criando…' : 'Criar usuário'}
            </button>
          </div>
        </form>
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">Usuários cadastrados</h2>
        </header>

        {carregandoUsuarios ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card p-5 animate-pulse">
                <div className="h-4 bg-surface-subtle rounded w-1/3 mb-3" />
                <div className="h-3 bg-surface-subtle rounded w-1/2 mb-5" />
                <div className="h-10 bg-surface-subtle rounded" />
              </div>
            ))}
          </div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className="card p-8 flex flex-col items-center text-center">
            <UsersIcon size={28} className="text-ink-faint mb-2" />
            <p className="text-sm text-ink-muted">Nenhum usuário encontrado com os filtros atuais.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {usuariosPaginados.map((usuario) => {
              const ehUsuarioLogado = usuario.id === usuarioLogado?.id;
              const processando = acaoUsuarioId === usuario.id;

              return (
                <article key={usuario.id} className="card p-5 flex flex-col gap-4">
                  <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink truncate">{usuario.nome}</h3>
                      <p className="text-sm text-ink-subtle truncate mt-0.5">{usuario.email}</p>
                      <p className="text-xs text-ink-faint mt-1">
                        Criado em {new Date(usuario.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <PerfilChip perfil={usuario.perfil} />
                      <span className={usuario.ativo ? 'chip-success' : 'chip-neutral'}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      {ehUsuarioLogado && <span className="chip-info">Seu usuário</span>}
                    </div>
                  </header>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-line/60">
                    {/* Perfil */}
                    <div className="flex flex-col gap-1.5">
                      <span className="label flex items-center gap-1.5"><ShieldCheck size={12} /> Perfil</span>
                      <div className="flex gap-2">
                        <select
                          value={perfisEdicao[usuario.id] || usuario.perfil}
                          onChange={(e) =>
                            setPerfisEdicao((prev) => ({ ...prev, [usuario.id]: e.target.value }))
                          }
                          className="input flex-1"
                          disabled={ehUsuarioLogado || processando}
                        >
                          <option value="atendente">Atendente</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="admin">Administrador</option>
                        </select>
                        <button
                          type="button"
                          className="btn-secondary shrink-0"
                          onClick={() => salvarPerfilUsuario(usuario.id)}
                          disabled={ehUsuarioLogado || processando}
                        >
                          {processando ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
                        </button>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex flex-col gap-1.5">
                      <span className="label flex items-center gap-1.5"><Power size={12} /> Status</span>
                      <button
                        type="button"
                        onClick={() => alternarStatusUsuario(usuario)}
                        disabled={ehUsuarioLogado || processando}
                        className={usuario.ativo ? 'btn-danger' : 'btn-primary'}
                      >
                        {processando ? <Loader2 size={14} className="animate-spin" /> :
                          ehUsuarioLogado ? 'Seu usuário' :
                          usuario.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </div>

                    {/* Senha */}
                    <div className="flex flex-col gap-1.5">
                      <span className="label flex items-center gap-1.5"><KeyRound size={12} /> Nova senha</span>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          placeholder="Min. 6 caracteres"
                          value={senhasEdicao[usuario.id] || ''}
                          onChange={(e) =>
                            setSenhasEdicao((prev) => ({ ...prev, [usuario.id]: e.target.value }))
                          }
                          className="input flex-1"
                          disabled={ehUsuarioLogado || processando}
                        />
                        <button
                          type="button"
                          className="btn-secondary shrink-0"
                          onClick={() => alterarSenhaUsuario(usuario.id)}
                          disabled={ehUsuarioLogado || processando}
                        >
                          {processando ? <Loader2 size={14} className="animate-spin" /> : 'Trocar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {usuariosFiltrados.length > usuariosPorPagina && (
          <div className="flex items-center justify-between bg-surface rounded-xl border border-line/60 px-4 py-3">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPaginaUsuarios((p) => Math.max(p - 1, 1))}
              disabled={paginaSegura === 1}
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="text-xs text-ink-subtle">
              Página <strong className="text-ink">{paginaSegura}</strong> de <strong className="text-ink">{totalPaginas}</strong>
              {' • '}{usuariosFiltrados.length} usuário(s)
            </span>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPaginaUsuarios((p) => Math.min(p + 1, totalPaginas))}
              disabled={paginaSegura === totalPaginas}
            >
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
